import { get } from 'lodash';
import { Schema } from 'normalizr';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { REQUEST_METHODS } from '../constants';
import { createActionSet } from '../helpers/actions';
import { API_REQUEST } from '../middlewares/api-middleware';
import { ActionSet, NormalisedData } from '../types';
import { resolveCachedUrls } from './cache-invalidation-utils';
import {
  DELETE_NORMALISED,
  DELETE_NORMALISED_FLUSH,
  NORMALISATION_FLAGS,
  PREFIX_DELTE_FROM_REMOTE,
  PREFIX_FETCH_FROM_REMOTE,
  PUT_NORMALISED
} from './constants';
import { DispatchAny, NormalisedStoreState } from './types';

/**
 * Abstract action to fetch from remote server.
 *
 * @param {Schema} schema
 * @param {string} path
 * @param {ActionSet} actionSet
 * @returns {ThunkAction}
 */
export const fetchAction = (schema: Schema, path: string, actionSet: ActionSet) => async (dispatch: ThunkDispatch<unknown, unknown, Action>) => {
  const tempActionSet: ActionSet = createActionSet(`${PREFIX_FETCH_FROM_REMOTE}${actionSet.ACTION}`);
  return dispatch({
    type: tempActionSet.ACTION,
    payload: {
      request: {
        url: path
      }
    },
    meta: {
      schema,
      flag: API_REQUEST
    }
  });
};

/**
 * This updates the normalised store with the given payload.
 *
 * @param {NormalisedData<T>} normalisedData
 * @param {string} entity - entity name, e.g. 'Organisation
 * @param {path} path - api url
 * @returns {ThunkAction}
 */
export const updateNormalisedStore = (
  normalisedData: NormalisedData<unknown>,
  entity?: string,
  path?: string
): DispatchAny => (dispatch: ThunkDispatch<NormalisedStoreState, unknown, Action>) =>
  dispatch({
    payload: {
      normalisedData,
      entity,
      path
    },
    type: PUT_NORMALISED.ACTION
  });

/**
 * Abstract action to delete from remote server.
 *
 * @param {string} path
 * @param {ActionSet} actionSet
 * @param {Schema} schema
 * @returns {ThunkAction}
 */
export const deleteAction = (path: string, actionSet: ActionSet, schema: Schema = {}) => async (dispatch: ThunkDispatch<unknown, unknown, Action>) => {
  const tempActionSet: ActionSet = createActionSet(`${PREFIX_DELTE_FROM_REMOTE}${actionSet.ACTION}`);
  return dispatch({
    type: tempActionSet.ACTION,
    payload: {
      request: {
        method: REQUEST_METHODS.DELETE,
        url: path
      }
    },
    meta: {
      schema,
      flag: API_REQUEST
    }
  });
};

/**
 * This updates the normalised store with the given payload.
 *
 * @param {NormalisedStoreState} normalised
 * @param {boolean} flushEntity
 * @returns {ThunkAction}
 */
export const deleteNormalisedStore = (normalised: NormalisedStoreState, flushEntity = false): DispatchAny => {
  const type = flushEntity ? DELETE_NORMALISED_FLUSH : DELETE_NORMALISED.ACTION;

  return (dispatch: ThunkDispatch<NormalisedStoreState, unknown, Action>) =>
    dispatch({
      type,
      payload: {
        normalised
      }
    });
};

/**
 * Flags given path or entity paths (after resolution) as stale so that next time the
 * given resource is requested, it will go to the server and do so.
 *
 * This is a redux action, so treat it as you would any other.
 *
 * @example: `markAsStale('Organisation');`
 *  this would add any paths that have previously been requested with the return type
 *  of "Organisation", so for example will add the following urls as stale:
 *    [
 *      "/workspaces/5648b594-2b90-444f-8ef1-f96348dbdcd4/organisations/"
 *      "/workspaces/5648b594-2b90-444f-8ef1-f96348dbdcd4/organisations/3fab8509-5f08-4847-b426-383d73098f52/"
 *    ]
 *
 * @example: `markAsStale('/workspaces/5648b594-2b90-444f-8ef1-f96348dbdcd4/organisations/');`
 *  this would add just the specific path given to the stale paths store.
 *
 * @param {string} pathOrEntity
 * @returns {ThunkAction}
 */
export const markAsStale = (pathOrEntity: string): DispatchAny => (dispatch: ThunkDispatch<NormalisedStoreState, unknown, Action>, getState: () => unknown) => {
  const stalePaths = resolveCachedUrls(get(getState(), 'normalised', {}), pathOrEntity);

  return dispatch({
    type: NORMALISATION_FLAGS.MARK_AS_STALE,
    payload: stalePaths
  });
};
